import type { Middleware } from 'redux';
import { posthog } from 'posthog-js';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { ModalActionPayLoadType } from '../features/globalModals/globalModalsSlice';
import { UserSettings, setUser } from '../features/settings/settingsSlice';
import { pushModal } from '../features/globalModals/globalModalsSlice';
import config from '../../config';
import { RootState } from '../reducers';
import { GetSubscriptionResponseData } from '../services/subscriptionServices';
import { checkUpgradeStatusThunk } from '../features/plan/thunk';
import { trackMediaAction } from '../features/library/actions/mediaActions/mediaActionsSlice';
import type { MediaActionPayload } from '../features/library/actions/mediaActions/mediaActionsSlice';

// Initialize PostHog (make sure you replace 'YOUR_POSTHOG_API_KEY' and 'YOUR_POSTHOG_API_HOST' with actual values)
const { posthogApiKey, posthogApiHost } = config;
posthog.init(posthogApiKey, {
  api_host: posthogApiHost,
  capture_pageview: false, // disable automatic pageview tracking
  capture_pageleave: false, // disable automatic pageleave tracking
});

const posthogMiddleware: Middleware<{}, RootState> = ({ getState }) => (
  next
) => (
  action: PayloadAction<
    | UserSettings
    | ModalActionPayLoadType
    | GetSubscriptionResponseData
    | MediaActionPayload
  >
) => {
  const { plan } = getState();
  const { selectedPlan, sourceFeatureKey } = plan.upgrade;
  if (action.type === setUser.type) {
    const { name, email } = (action as PayloadAction<UserSettings>).payload;

    const [firstName, ...lastNameParts] = name.split(' ');
    const lastName = lastNameParts.join(' ');

    posthog.capture('set-user', {
      $set_once: { firstName, lastName, email },
      $set: { firstName, lastName, email },
    });
  }
  if (action.type === pushModal.type) {
    const { type } = (action as PayloadAction<ModalActionPayLoadType>).payload;
    if (type === 'upsell-modal') {
      posthog.capture('upgrade-modal', { selectedPlan, sourceFeatureKey });
    }
  }
  if (action.type === checkUpgradeStatusThunk.fulfilled.type) {
    const {
      subscription,
    } = (action as PayloadAction<GetSubscriptionResponseData>).payload;
    const { package: upgradedPlan } = subscription;
    posthog.capture('post-payment', { plan: upgradedPlan, sourceFeatureKey });
  }
  if (action.type === trackMediaAction.type) {
    const {
      actionKey,
      actionLabel,
      teamId,
      folderId,
    } = (action as PayloadAction<MediaActionPayload>).payload;
    posthog.capture('media-action-click', {
      actionKey,
      actionLabel,
      teamId,
      folderId,
    });
  }

  return next(action);
};

export default posthogMiddleware;
